import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { PartySearch } from 'views/containers'
import { FocusMode } from 'views/components'
import { NewContactForm, ContactsProvider } from 'modules/contacts'
import { TPartyDetails } from 'modules/api/party/types'
import { partyApiEvents, partyApiSelectors } from 'modules/api/party'

interface PartySearchOrCreateProps {
  name: string
  label?: string
  placeholder?: string
  tags?: string
  value?: string
  error?: string
  disabled?: boolean
  onSelect: (party: any) => void
  onAddNewContact?: () => void
  showAddNewContact?: boolean
  autoSelectNewContact?: boolean
  /**
   * Labels the result sets as headed groups (e.g. ['Your Team', 'All Contacts']).
   * Forwarded to PartySearch, which builds the labelled groups (team = parties tagged with any
   * `orderByTags` tag, everyone else follows) — mirroring Search's `toGroups` normalisation.
   */
  resultSetHeadings?: React.ReactNode[]
  // Other PartySearch props
  [key: string]: any
}

export const PartySearchOrCreate: React.FC<PartySearchOrCreateProps> = ({
  name,
  label,
  placeholder = 'Search contacts',
  tags = '',
  value = '',
  error,
  disabled = false,
  onSelect,
  onAddNewContact,
  showAddNewContact = false,
  autoSelectNewContact = false,
  ...otherProps
}) => {
  const dispatch = useDispatch()

  // Stable per-instance initiator id used to disambiguate this component's
  // createParty_request from any other in-flight create in the app.
  const initiatorRef = useRef(`partySearchOrCreate-${Date.now()}-${Math.random().toString(36).slice(2)}`)
  const didCreateRef = useRef(false)

  // Local state
  const [isModalOpen, setIsModalOpen] = useState(false)
  // Only use value if it's a non-empty string, otherwise default to empty
  const [searchValue, setSearchValue] = useState(() => {
    // Use function form to ensure fresh evaluation on mount
    return value && typeof value === 'string' && value.trim() !== '' ? value : ''
  })

  const createdParty = useSelector((state: any) =>
    partyApiSelectors.getCreatedPartyByInitiator(state)(initiatorRef.current),
  )

  // Update search value when prop changes (only if it's a valid non-empty string)
  useEffect(() => {
    const trimmedValue = typeof value === 'string' ? value.trim() : ''
    if (trimmedValue && trimmedValue.length > 0) {
      setSearchValue(trimmedValue)
    } else {
      // Explicitly clear if value becomes empty/null/undefined
      setSearchValue('')
    }
  }, [value])

  // React to a successful create attributable to *this* component instance.
  useEffect(() => {
    if (!createdParty) return

    const partyType = createdParty.person ? 'person' : createdParty.company ? 'company' : null
    const partyDetails: TPartyDetails | undefined = createdParty.person ?? createdParty.company
    if (partyType && partyDetails && autoSelectNewContact) {
      const formattedParty = {
        text: partyDetails.firstName
          ? `${partyDetails.firstName} ${partyDetails.lastName}`
          : partyDetails.companyName || '',
        partyType,
        ...createdParty,
        id: partyDetails.id,
      }
      setSearchValue(formattedParty.text)
      onSelect(formattedParty)
    }
    setIsModalOpen(false)
    dispatch(partyApiEvents.clearCreatedByInitiator(initiatorRef.current))
    didCreateRef.current = false // reset so unmount cleanup doesn't double-clear
  }, [createdParty, autoSelectNewContact, onSelect, dispatch])

  // Cleanup on unmount: avoid leaking an in-flight create into the slice.
  useEffect(
    () => () => {
      if (didCreateRef.current) {
        dispatch(partyApiEvents.clearCreatedByInitiator(initiatorRef.current))
      }
    },
    [dispatch],
  )

  const handleAddNewContact = useCallback(() => {
    if (onAddNewContact) {
      onAddNewContact()
    } else {
      setIsModalOpen(true)
    }
  }, [onAddNewContact])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handlePartySelect = useCallback(
    (party: any) => {
      setSearchValue(party.text)
      onSelect(party)
    },
    [onSelect],
  )

  const handleContactCreated = useCallback(
    (values: any) => {
      didCreateRef.current = true
      dispatch(partyApiEvents.createParty_request(values, { initiator: initiatorRef.current }))
      // The listener effect closes the modal on success.
    },
    [dispatch],
  )

  const WrappedNewContactForm = useMemo(() => {
    return function ContactFormWrapper() {
      return (
        <ContactsProvider match={{ params: {} }}>
          <NewContactForm
            onSubmit={handleContactCreated}
            allowSearchable={false}
            getButtonText={(values: any) => {
              if (values?.id) {
                return 'Select existing contact'
              }
              return autoSelectNewContact ? 'Create and select contact' : 'Create contact'
            }}
          />
        </ContactsProvider>
      )
    }
  }, [handleContactCreated, autoSelectNewContact])

  return (
    <>
      <PartySearch
        name={name}
        label={label}
        placeholder={placeholder}
        tags={tags}
        value={searchValue}
        error={error}
        disabled={disabled}
        onResultSelect={handlePartySelect}
        showAddNewContact={showAddNewContact}
        onAddNewContact={handleAddNewContact}
        showNoResults={true}
        {...otherProps}
      />

      {isModalOpen &&
        createPortal(
          <FocusMode
            isOpen={isModalOpen}
            title="Create contact"
            ContentComponent={WrappedNewContactForm}
            onClose={handleCloseModal}
          />,
          document.body,
        )}
    </>
  )
}

export default PartySearchOrCreate
